import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, Modal, Alert, ActivityIndicator, Platform, ScrollView } from 'react-native';
import { uploadForecastFiles } from '../../../api/AllAddress ';
import { useAlertModal } from '../../../common/hooks';
import { FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as XLSX from 'xlsx';

interface FileInfo {
  name: string;
  size: number;
  type: string;
}

interface UploadXlsModalProps {
  isModalVisible: boolean;
  closeModal: () => void;
  onFileUploaded: (data: any[][], fileInfo: FileInfo) => void;
}

const UploadXlsModal: React.FC<UploadXlsModalProps> = ({ isModalVisible, closeModal, onFileUploaded }) => {
  const { showAlert } = useAlertModal();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<FileInfo | null>(null);
  const [parsedData, setParsedData] = useState<any[][] | null>(null);
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [parsedSheets, setParsedSheets] = useState<{ [sheetName: string]: any[][] }>({});
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserId = async () => {
      const storedUserId = await AsyncStorage.getItem('user-id');
      setUserId(storedUserId);
    };
    fetchUserId();
  }, []);

  const handleFileSelect = async (): Promise<void> => {
    if (Platform.OS === 'web') {
      // Web file selection
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls';
      input.onchange = async (e: Event) => {
        const target = e.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file) {
          await processFile(file);
        }
      };
      input.click();
    } else {
      // Mobile file selection would require expo-document-picker
      showAlert('Info', 'File selection not supported on mobile');
    }
  };

  const processFile = async (file: File): Promise<void> => {
    setIsLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const result = e.target?.result;
          if (!result) throw new Error('Failed to read file');
  
          const data = result instanceof ArrayBuffer 
            ? new Uint8Array(result) 
            : new Uint8Array(new ArrayBuffer(0));
  
          const wb = XLSX.read(data, { type: 'array', raw: false });
          setWorkbook(wb);
  
          const sheetsData: { [sheetName: string]: any[][] } = {};
          wb.SheetNames.forEach(sheetName => {
            const worksheet = wb.Sheets[sheetName];
            sheetsData[sheetName] = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });
          });
          setParsedSheets(sheetsData);
  
          const firstSheetName = wb.SheetNames[0];
          setParsedData(sheetsData[firstSheetName]);
  
          setSelectedFile({ name: file.name, size: file.size, type: file.type });
          setIsLoading(false);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          setIsLoading(false);
          showAlert('Error', 'Failed to parse Excel file.');
        }
      };
  
      reader.onerror = () => {
        setIsLoading(false);
        showAlert('Error', 'Failed to read file');
      };
  
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error processing file:', error);
      setIsLoading(false);
      showAlert('Error', 'Failed to process file');
    }
  };  

  const handleUpload = async (): Promise<void> => {
    if (!parsedData || !selectedFile) {
      showAlert('Error', 'Please select a file first');
      return;
    }
    
    setIsLoading(true);
    try {
      // Create FormData properly for file upload
      const formData = new FormData();
      
      if (Platform.OS === 'web' && parsedData) {
        const newWorkbook = XLSX.utils.book_new();
        for (const sheetName in parsedSheets) {
          const sheetData = parsedSheets[sheetName];
          const newSheet = XLSX.utils.aoa_to_sheet(sheetData);
          XLSX.utils.book_append_sheet(newWorkbook, newSheet, sheetName);
        }
        
        const rebuiltWorkbookData = XLSX.write(newWorkbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([rebuiltWorkbookData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        formData.append('file', blob, selectedFile.name);
        formData.append('user_id', userId);
      }
      
      uploadForecastFiles(formData, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            showAlert('success', jsonRes.message);
            onFileUploaded(parsedData, selectedFile);
            handleClose();
            break;
          case 409:          
              showAlert('error', jsonRes.error);
            break;
          default:
            if (jsonRes && jsonRes.error) {
              showAlert('error', jsonRes.error);
            }
            handleClose();
            break;
        }
        setIsLoading(false);
      });

    } catch (error) {
      console.error('Upload error:', error);
      setIsLoading(false);
      showAlert('Error', 'Failed to upload file');
    }
  };

  const handleClose = (): void => {
    setSelectedFile(null);
    setParsedData(null);
    setWorkbook(null);
    setIsLoading(false);
    closeModal();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal
      visible={isModalVisible}
      transparent={true}
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          {/* Fixed Header */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Upload Excel File</Text>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <FontAwesome5 name="times" size={20} color="#666" />
            </TouchableOpacity>
          </View>

          {/* Scrollable Body */}
          <ScrollView 
            style={styles.modalBody}
            contentContainerStyle={styles.modalBodyContent}
            showsVerticalScrollIndicator={true}
          >
            {/* File Selection Area */}
            <TouchableOpacity
              style={styles.uploadArea}
              onPress={handleFileSelect}
              disabled={isLoading}
            >
              <FontAwesome5 
                name="file-excel" 
                size={48} 
                color={selectedFile ? "#28a745" : "#6c757d"} 
              />
              <Text style={styles.uploadText}>
                {selectedFile ? "File Selected" : "Click to select Excel file"}
              </Text>
              <Text style={styles.uploadSubtext}>
                Supports .xlsx and .xls files
              </Text>
            </TouchableOpacity>

            {/* Loading Indicator */}
            {isLoading && !selectedFile && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#007bff" />
                <Text style={styles.loadingText}>Processing file...</Text>
              </View>
            )}

            {/* File Info */}
            {selectedFile && (
              <View style={styles.fileInfo}>
                <View style={styles.fileInfoRow}>
                  <Text style={styles.fileInfoLabel}>File:</Text>
                  <Text style={styles.fileInfoValue} numberOfLines={2}>{selectedFile.name}</Text>
                </View>
                <View style={styles.fileInfoRow}>
                  <Text style={styles.fileInfoLabel}>Size:</Text>
                  <Text style={styles.fileInfoValue}>{formatFileSize(selectedFile.size)}</Text>
                </View>
                {workbook && (
                  <View style={styles.fileInfoRow}>
                    <Text style={styles.fileInfoLabel}>Sheets:</Text>
                    <Text style={styles.fileInfoValue}>{workbook.SheetNames.length}</Text>
                  </View>
                )}
                {parsedData && (
                  <View style={styles.fileInfoRow}>
                    <Text style={styles.fileInfoLabel}>Rows:</Text>
                    <Text style={styles.fileInfoValue}>{parsedData.length}</Text>
                  </View>
                )}
              </View>
            )}
          </ScrollView>

          {/* Fixed Footer */}
          <View style={styles.modalFooter}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={handleClose}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[
                styles.uploadButton,
                (!parsedData || isLoading) && styles.uploadButtonDisabled
              ]}
              onPress={handleUpload}
              disabled={!parsedData || isLoading}
            >
              {isLoading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Text style={styles.uploadButtonText}>Upload</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = {
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    paddingVertical: 20,
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 8,
    width: '90%' as any,
    maxWidth: 600,
    maxHeight: '90%' as any,
    flexShrink: 1,
  },
  modalHeader: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold' as const,
    color: '#333',
  },
  closeButton: {
    padding: 5,
  },
  modalBody: {
    flex: 1,
    maxHeight: 500,
  },
  modalBodyContent: {
    padding: 20,
    paddingBottom: 0,
  },
  uploadArea: {
    borderWidth: 2,
    borderColor: '#dee2e6',
    borderStyle: 'dashed' as const,
    borderRadius: 8,
    padding: 40,
    alignItems: 'center' as const,
    marginBottom: 20,
    backgroundColor: '#f8f9fa',
  },
  uploadText: {
    fontSize: 16,
    color: '#495057',
    marginTop: 10,
    fontWeight: '500' as const,
    textAlign: 'center' as const,
  },
  uploadSubtext: {
    fontSize: 12,
    color: '#6c757d',
    marginTop: 5,
    textAlign: 'center' as const,
  },
  loadingContainer: {
    alignItems: 'center' as const,
    paddingVertical: 20,
  },
  loadingText: {
    marginTop: 10,
    color: '#007bff',
    fontSize: 14,
  },
  fileInfo: {
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 8,
    marginBottom: 20,
  },
  fileInfoRow: {
    flexDirection: 'row' as const,
    marginBottom: 8,
    alignItems: 'flex-start' as const,
  },
  fileInfoLabel: {
    fontWeight: 'bold' as const,
    width: 60,
    color: '#495057',
  },
  fileInfoValue: {
    flex: 1,
    color: '#495057',
  },
  modalFooter: {
    flexDirection: 'row' as const,
    justifyContent: 'flex-end' as const,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
  },
  cancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 4,
    marginRight: 10,
    borderWidth: 1,
    borderColor: '#6c757d',
  },
  cancelButtonText: {
    color: '#6c757d',
    fontWeight: '500' as const,
  },
  uploadButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 4,
    backgroundColor: '#007bff',
    minWidth: 80,
    alignItems: 'center' as const,
  },
  uploadButtonDisabled: {
    backgroundColor: '#6c757d',
  },
  uploadButtonText: {
    color: 'white',
    fontWeight: '500' as const,
  },
};

export default UploadXlsModal;